import React, { useCallback, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { app } from "../../firebase";
import { useAuthCtx } from "../../auth/AuthProvider";
import { useRouteTitle } from "../../hooks/useRouteTitle";
import BrandWordmark from "../../components/brand/BrandWordmark";
import { getDefaultDashboard } from "@/lib/auth/smartRouting";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { loginLogger } from "@/lib/auth/loginLogger";
import { hasProClaim } from "@/lib/auth/claimsUtils";
import { runLoginBootstrap } from "@/lib/auth/bootstrapOrchestrator";

export default function LoginPage() {
  useRouteTitle("Login");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bootstrapStatus, setBootstrapStatus] = useState<
    'idle' | 'authenticating' | 'setting-up' | 'completing'
  >('idle');
  const [bootstrapWarning, setBootstrapWarning] = useState<string | null>(null);
  const nav = useNavigate();
  const location = useLocation();
  const fromState = location.state as { from?: { pathname?: string; search?: string; hash?: string } } | undefined;
  
  // Check for email verification status
  const verificationStatus = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('verified');
  }, [location.search]);

  const requestedPath = useMemo(() => {
    const disallowed = new Set(["/login", "/signup", "/reset-password"]);
    const normalize = (value?: string | null) => {
      if (!value) return undefined;
      if (!value.startsWith("/")) return undefined;
      const base = value.split("?")[0];
      if (disallowed.has(base)) return undefined;
      return value;
    };

    const params = new URLSearchParams(location.search);
    const paramTarget = normalize(params.get("returnTo") ?? params.get("next"));
    const stateLocation = fromState?.from;
    const stateTarget = stateLocation?.pathname
      ? normalize(`${stateLocation.pathname}${stateLocation.search ?? ""}${stateLocation.hash ?? ""}`)
      : undefined;

    // Don't default to /cases - let getDefaultDashboard determine the right path based on user claims
    return paramTarget ?? stateTarget;
  }, [fromState, location.search]);

  const encodedReturnTo = requestedPath ? `?returnTo=${encodeURIComponent(requestedPath)}` : "";
  const subscribeLink = `/subscribe${encodedReturnTo}`;
  const billingLink = `/billing${encodedReturnTo}`;
  const originState = useMemo(
    () => ({ pathname: location.pathname, search: location.search, hash: location.hash }),
    [location.hash, location.pathname, location.search]
  );
  const goToSubscribe = useCallback(() => {
    nav(subscribeLink, { state: { from: originState } });
  }, [nav, originState, subscribeLink]);
  const goToBilling = useCallback(() => {
    nav(billingLink, { state: { from: originState } });
  }, [billingLink, nav, originState]);
  const { user, initializing, signOutUser, claims } = useAuthCtx();
  const checkingAuth = initializing && !!user;
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [isPro, setIsPro] = React.useState<boolean | null>(null);
  const [isDemo, setIsDemo] = React.useState<boolean | null>(null);
  const [defaultRoute, setDefaultRoute] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    
    if (!user) {
      setIsAdmin(null);
      setIsPro(null);
      setIsDemo(null);
      setDefaultRoute(null);
      return;
    }
    
    // If user just verified email or just logged in, force refresh token to get updated claims
    const shouldRefresh = verificationStatus === 'true';
    
    const loadClaims = (forceRefresh: boolean) => {
      user.getIdTokenResult(forceRefresh).then((tokenResult) => {
        if (isMounted) {
          setIsAdmin(tokenResult.claims.admin === true);
          const hasPro = hasProClaim(tokenResult.claims);
          setIsPro(hasPro);
          setIsDemo(tokenResult.claims.demo === true);
          
          // EMERGENCY FIX: Retry logic disabled because fireOnLoginSetClaims() is disabled
          // Claims may be stale but login will still work - users can manually refresh if needed
        }
      }).catch(() => {
        if (isMounted) {
          setIsAdmin(false);
          setIsPro(false);
          setIsDemo(false);
        }
      });
    };
    
    loadClaims(shouldRefresh);
    
    return () => { 
      isMounted = false;
    };
  }, [user, verificationStatus]);

  React.useEffect(() => {
    let isMounted = true;
    
    if (!user || isAdmin !== true) {
      setDefaultRoute(null);
      return;
    }
    
    const route = getDefaultDashboard(claims);
    if (isMounted) {
      setDefaultRoute(route);
    }

    return () => { isMounted = false; };
  }, [user, isAdmin, requestedPath]);

  if (checkingAuth) {
    return <GateMessage>Checking session…</GateMessage>;
  }

  if (user) {
    // Admins bypass subscription check
    if (isAdmin === null || isPro === null || isDemo === null) {
      return <GateMessage>Verifying access…</GateMessage>;
    }
    
    if (isAdmin) {
      // Smart routing: Redirect to appropriate dashboard based on highest privilege
      if (!defaultRoute) {
        return <GateMessage>Loading…</GateMessage>;
      }
      
      return <Navigate to={defaultRoute} replace />;
    }

    // Pro users bypass subscription check and go to Pro dashboard
    if (isPro) {
      return <Navigate to="/pro/dashboard" replace />;
    }

    // Demo users bypass subscription check
    if (isDemo) {
      return <Navigate to={requestedPath} replace />;
    }

    // CRITICAL FIX: Login page should NOT check subscription status
    // That's the job of RequireSubscription guard
    // If user is logged in, redirect them to requested path
    // RequireSubscription will handle redirecting to /billing if needed
    return <Navigate to={requestedPath || "/cases"} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[LoginPage] onSubmit called - email:', email, 'busy:', busy);
    if (busy) return;
    
    // Reset state
    setErr(null);
    setBootstrapWarning(null);
    setBusy(true);
    setBootstrapStatus('authenticating');

    // Start login attempt with correlation ID
    const attemptId = loginLogger.startAttempt(email);

    try {
      // Phase 1: Firebase Authentication
      loginLogger.log('FIREBASE_SIGNIN_START');
      const auth = getAuth(app);
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pwd);
      loginLogger.log('FIREBASE_SIGNIN_OK', { uid: cred.user.uid });

      // Phase 2: Determine destination from current token claims (fast, reads from cache).
      // Navigation must NOT wait for bootstrap — bootstrap CORS failures were causing a
      // 10+ second delay before nav() fired. During that wait the render-path <Navigate>
      // correctly routed the user to their case, but then this late nav('/cases') call
      // overrode it, causing the visible redirect-back-to-cases bug.
      setBootstrapStatus('completing');
      const defaultRoute = getDefaultDashboard(claims);

      // Phase 3: Navigate to the user's default destination.
      // Subscription enforcement is handled by RequireSubscription, which
      // reads live state — not a stale Firestore snapshot taken at login time.
      loginLogger.log('LOGIN_SUCCESS_NAVIGATE', { route: defaultRoute, reason: 'immediate' });
      nav(defaultRoute, { replace: true });

      // Phase 4: Bootstrap in background — ensures user doc and claims are propagated.
      // Runs AFTER nav() so it can never delay or conflict with routing.
      // Claims update will be reflected on the user's next token refresh.
      runLoginBootstrap(cred.user).then((result) => {
        if (result.warnings.length > 0) {
          console.warn('[LoginPage] Background bootstrap warnings:', result.warnings);
        }
      }).catch((err) => {
        console.warn('[LoginPage] Background bootstrap failed (non-critical):', err);
      });

    } catch (error) {
      console.error('[LoginPage] onSubmit error:', error);
      
      // Determine error bucket (AUTH vs BOOTSTRAP vs UNKNOWN)
      const errorMessage = error instanceof Error ? error.message : 'unknown';
      const isAuthError = errorMessage.includes('auth/') || 
                          errorMessage.includes('wrong-password') ||
                          errorMessage.includes('user-not-found') ||
                          errorMessage.includes('invalid-credential');

      if (isAuthError) {
        loginLogger.log('FIREBASE_SIGNIN_ERR', { error: errorMessage }, 'AUTH');
        loginLogger.log('LOGIN_UI_ERROR_SHOWN', { bucket: 'AUTH' });
        setErr("Unable to sign in. Check your credentials and try again.");
      } else {
        // Bootstrap or network error - non-critical
        loginLogger.log('LOGIN_UI_ERROR_SHOWN', { bucket: 'BOOTSTRAP', error: errorMessage });
        setErr("Sign in succeeded but account setup is taking longer than expected. Please try again or contact support if the issue persists.");
      }

      setBusy(false);
      setBootstrapStatus('idle');
    } finally {
      loginLogger.clearAttempt();
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-transparent" data-testid="login-page-root">
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      
      <div data-testid="logged-out-state" className="sr-only">
        logged-out
      </div>
      
      <div className="relative w-full max-w-md rounded-2xl border border-orange-600/50 bg-slate-900/90 p-8 shadow-2xl backdrop-blur-sm">
        {/* BrandWordmark - Clickable to threadlock.ai */}
        <div className="mb-8 flex justify-center">
          <a href="https://threadlock.ai" target="_blank" rel="noopener noreferrer" aria-label="ThreadLock home page" className="transition hover:opacity-80">
            <BrandWordmark darkText={false} className="text-3xl" />
          </a>
        </div>
        
        <h1 className="mb-6 text-center text-2xl font-semibold text-white">Welcome back</h1>
        
        {verificationStatus === 'true' && (
          <div className="mb-4 rounded-md border border-green-500/60 bg-green-500/10 px-3 py-2 text-sm text-green-400" role="status">
            ✅ Email verified successfully! You can now sign in.
          </div>
        )}
        
        {verificationStatus === 'error' && (
          <div className="mb-4 rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert">
            ❌ Email verification failed. The link may be expired or invalid. Please request a new verification email.
          </div>
        )}
        
        {/* Bootstrap Status Indicator */}
        {bootstrapStatus !== 'idle' && (
          <div className="mb-4 rounded-md border border-blue-500/60 bg-blue-500/10 px-3 py-2 text-sm text-blue-400 flex items-center gap-2" role="status">
            <Loader2 className="animate-spin" size={16} />
            {bootstrapStatus === 'authenticating' && 'Authenticating...'}
            {bootstrapStatus === 'setting-up' && 'Setting up your account...'}
            {bootstrapStatus === 'completing' && 'Completing sign in...'}
          </div>
        )}

        {/* Bootstrap Warning (non-blocking) */}
        {bootstrapWarning && (
          <div className="mb-4 rounded-md border border-yellow-500/60 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-400" role="alert">
            ⚠️ {bootstrapWarning}
          </div>
        )}
        
        {err ? (
          <p 
            data-testid="login.error"
            className="mb-4 rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-sm text-red-400" 
            role="alert"
          >
            {err}
          </p>
        ) : null}
        
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm text-slate-300">
              Email
            </label>
            <input
              id="email"
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm text-slate-300">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 pr-12 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-pressed={showPassword}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-2 my-auto flex h-8 items-center justify-center rounded-md px-2 text-slate-400 transition hover:bg-slate-600/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              >
                {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="w-full rounded-2xl border border-transparent bg-orange-600 py-3 text-base font-semibold text-white transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500/60 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy}
          >
            {bootstrapStatus === 'authenticating' && "Authenticating…"}
            {bootstrapStatus === 'setting-up' && "Setting up account…"}
            {bootstrapStatus === 'completing' && "Completing sign in…"}
            {bootstrapStatus === 'idle' && !busy && "Sign in"}
            {bootstrapStatus === 'idle' && busy && "Signing in…"}
          </button>
        </form>
        
        <div className="mt-6 text-center text-sm text-slate-300">
          Forgot your password?{" "}
          <Link className="text-orange-400 underline hover:text-orange-300" to="/reset-password">
            Reset it
          </Link>
        </div>
        <div className="mt-2 text-center text-sm text-slate-300">
          Don't have an account?{" "}
          <Link className="text-orange-400 underline hover:text-orange-300" to="/subscribe">
            Sign up
          </Link>
        </div>
        <div className="mt-4 border-t border-slate-700 pt-4 text-center text-sm text-slate-400">
          Are you a legal professional?{" "}
          <Link className="text-purple-400 underline hover:text-purple-300" to="/legal-pro">
            Join our Pro Network
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}

function GateMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-transparent px-4">
      <div className="relative w-full max-w-md rounded-2xl border border-orange-600/50 bg-slate-900/90 p-6 text-center backdrop-blur-sm shadow-2xl">
        <div className="mb-6 flex justify-center">
          <a href="https://threadlock.ai" target="_blank" rel="noopener noreferrer" aria-label="ThreadLock home page" className="transition hover:opacity-80">
            <BrandWordmark darkText={false} className="text-2xl" />
          </a>
        </div>
        <div className="text-sm text-slate-300">
          {children}
        </div>
      </div>
    </div>
  );
}